import { useEffect, useState, useSyncExternalStore } from 'react';
import { AxiosError } from 'axios';
import { Wallet, Download } from '../../icons';
// 2026-09-13 (E) — 명세서 0건은 «연동 대기»가 아니라 «영역이 비었음» 이다(정본: EmptyState).
//   공용 SystemConnectingBanner 는 다른 화면이 쓰므로 그대로 두고, 이 화면만 갈아탄다.
import { EmptyState } from '../../components/shared/EmptyState';
import {
  listPayrollStatements, getPayrollStatement,
  type PayrollListItemDto, type PayrollDetailDto,
} from '../../api/payroll';
// Cycle 44 — lang-aware number locale
// Cycle 45 — getCurrencyFormatter (KRW currency 형식)
// Cycle 68 — payroll-i18n module (false negative 봉합)
import { getCurrencyFormatter } from '../../lib/base-i18n';
import { tPayroll, getPayrollLang, subscribePayrollLang } from '../../lib/payroll-i18n';
import './PayrollPage.css';

/**
 * PayrollPage — 시안 PayrollView (init-docs/WorkChat.jsx L4142~) 정합 (slfg --from-doc).
 *
 * 본 cycle (read-only wire):
 *   - GET /payroll/statements (list 6개월) + GET /payroll/statements/:id (detail) 실 API
 *   - 첫 항목 자동 선택, 사용자 클릭 시 detail fetch
 *   - PDF 저장 버튼 disabled — endpoint deferred (TD-5)
 *
 * out-of-scope: PDF endpoint, 컬럼 암호화 (PRD 명시지만 외부 급여 시스템 연동 정책 미정).
 *
 * Cycle 68: i18n 본체 적용 — cycle 67 의 false negative 5 page 중 PayrollPage (30 hardcoded).
 * payroll-i18n.ts 신규 모듈로 ko/en/ja 3 lang 매핑. SystemConnectingBanner default text 는
 * 향후 cycle (component-level i18n scope).
 */

function fmt(n: number): string {
  // Cycle 45 — payroll amount 는 KRW currency 형식 (₩1,234 등).
  // lang-aware locale (ko-KR / en-US / ja-JP) + currency=KRW.
  return getCurrencyFormatter('KRW').format(n);
}

export function PayrollPage() {
  useSyncExternalStore(subscribePayrollLang, getPayrollLang, getPayrollLang);
  const [items, setItems] = useState<PayrollListItemDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stmt, setStmt] = useState<PayrollDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 1) list fetch (mount 1회).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listPayrollStatements();
        if (cancelled) return;
        setItems(list);
        // 첫 항목 자동 선택.
        if (list.length > 0 && list[0]) setSelectedId(list[0].id);
      } catch (err) {
        if (cancelled) return;
        const ax = err as AxiosError<{ message?: string }>;
        setError(ax.response?.data?.message ?? tPayroll('payroll.err.list_load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2) selected detail fetch (selectedId 변경 시).
  useEffect(() => {
    if (!selectedId) { setStmt(null); return; }
    let cancelled = false;
    void (async () => {
      try {
        const detail = await getPayrollStatement(selectedId);
        if (cancelled) return;
        setStmt(detail);
      } catch (err) {
        if (cancelled) return;
        const ax = err as AxiosError<{ message?: string }>;
        setError(ax.response?.data?.message ?? tPayroll('payroll.err.statement_load'));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  if (loading) {
    return <main className="payroll-page"><h1 style={{ padding: 24 }}>{tPayroll('payroll.title')}</h1><p style={{ padding: 24 }}>{tPayroll('payroll.loading')}</p></main>;
  }

  if (items.length === 0) {
    return (
      <main className="payroll-page">
        <div className="payroll-page__banner-wrap">
          <h1 style={{ margin: '0 0 0.75rem' }}>{tPayroll('payroll.title')}</h1>
          {/* 오류가 있으면 «비었다» 보다 먼저 말한다 — 못 읽은 것을 빈 것으로 얼버무리지 않는다(정본 P3). */}
          {error && (
            <div role="alert" className="payroll-page__error">
              {error}
            </div>
          )}
          <EmptyState
            icon={Wallet}
            title={tPayroll('payroll.empty.title')}
            description={tPayroll('payroll.empty.desc')}
          />
        </div>
      </main>
    );
  }

  // trend chart (list 의 netPay 사용 — list 가 desc 순서, chart 는 oldest-first).
  const trend = items.slice().reverse();
  const minNet = Math.min(...trend.map((t) => t.netPay));
  const maxNet = Math.max(...trend.map((t) => t.netPay));
  const range = maxNet - minNet || 1;

  return (
    <main className="payroll-page">
      <div className="payroll-page__banner-wrap">
        <h1 style={{ margin: '0 0 0.75rem' }}>{tPayroll('payroll.title')}</h1>
        {error && (
          <div role="alert" className="payroll-page__error">
            {error}
          </div>
        )}
      </div>

      <div className="payroll-page__body">
        {/* 월 선택 사이드바 */}
        <aside className="payroll-sidebar" aria-labelledby="month-picker">
          <div className="payroll-sidebar__header">
            <div className="payroll-sidebar__icon">
              <Wallet size={17} aria-hidden />
            </div>
            <div>
              <h2 id="month-picker" className="payroll-sidebar__heading">{tPayroll('payroll.sidebar.heading')}</h2>
              <div className="payroll-sidebar__sub">{tPayroll('payroll.sidebar.sub')}</div>
            </div>
          </div>
          <ul className="payroll-sidebar__list">
            {items.map((it, i) => (
              <li key={it.id}>
                <button
                  className="payroll-sidebar__btn"
                  onClick={() => setSelectedId(it.id)}
                  aria-current={it.id === selectedId ? 'true' : undefined}
                >
                  <span>{it.label}</span>
                  {i === 0 && (
                    <span className="payroll-sidebar__latest-badge" aria-hidden>{tPayroll('payroll.sidebar.latest_badge')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* 명세 본문 */}
        <section className="payroll-detail" aria-labelledby="statement">
          {!stmt ? (
            <div className="payroll-detail__inner"><p>{tPayroll('payroll.loading')}</p></div>
          ) : (
            <div className="payroll-detail__inner">
              <header className="payroll-detail__header">
                <div>
                  <h2 id="statement" className="payroll-detail__title">{tPayroll('payroll.detail.title', { year: stmt.year, month: String(stmt.month).padStart(2, '0') })}</h2>
                  <p className="payroll-detail__subtitle">{tPayroll('payroll.detail.pay_date', { date: stmt.payDate })}</p>
                </div>
                <button className="payroll-detail__download" disabled title={tPayroll('payroll.detail.btn_pdf_disabled_title')}>
                  <Download size={13} aria-hidden /> {tPayroll('payroll.detail.btn_pdf')}
                </button>
              </header>

              {/* 실수령액 gradient 카드 */}
              <div className="payroll-net-card">
                <div className="payroll-net-card__label">{tPayroll('payroll.net_card.label')}</div>
                <div className="payroll-net-card__amount">
                  {fmt(stmt.netPay)}
                  <span className="payroll-net-card__amount-unit">{tPayroll('payroll.net_card.unit_won')}</span>
                </div>
                <div className="payroll-net-card__split">
                  <div className="payroll-net-card__split-item">{tPayroll('payroll.net_card.total_earnings')} <strong>{fmt(stmt.earnings.total)}</strong></div>
                  <div className="payroll-net-card__split-item">{tPayroll('payroll.net_card.total_deductions')} <strong>{fmt(stmt.deductions.total)}</strong></div>
                </div>
              </div>

              {/* 지급 + 공제 grid */}
              <div className="payroll-tables">
                <div className="payroll-table-card">
                  <div className="payroll-table-card__header">
                    <span className="payroll-table-card__icon--earnings" aria-hidden>▲</span>
                    {tPayroll('payroll.tables.earnings_heading')}
                  </div>
                  <PayRow label={tPayroll('payroll.tables.row.base_salary')} amount={stmt.earnings.baseSalary} />
                  <PayRow label={tPayroll('payroll.tables.row.overtime')} amount={stmt.earnings.overtimePay} />
                  <PayRow label={tPayroll('payroll.tables.row.bonus')} amount={stmt.earnings.bonus} />
                  <div className="payroll-table-card__total">
                    <span className="payroll-table-card__total-label">{tPayroll('payroll.tables.total_label')}</span>
                    <span className="payroll-table-card__total-value payroll-table-card__total-value--earnings">
                      {fmt(stmt.earnings.total)}
                    </span>
                  </div>
                </div>

                <div className="payroll-table-card">
                  <div className="payroll-table-card__header">
                    <span className="payroll-table-card__icon--deductions" aria-hidden>▼</span>
                    {tPayroll('payroll.tables.deductions_heading')}
                  </div>
                  <PayRow label={tPayroll('payroll.tables.row.income_tax')} amount={stmt.deductions.incomeTax} negate />
                  <PayRow label={tPayroll('payroll.tables.row.national_pension')} amount={stmt.deductions.nationalPension} negate />
                  <PayRow label={tPayroll('payroll.tables.row.health_insurance')} amount={stmt.deductions.healthInsurance} negate />
                  <PayRow label={tPayroll('payroll.tables.row.employment_insurance')} amount={stmt.deductions.employmentInsurance} negate />
                  <div className="payroll-table-card__total">
                    <span className="payroll-table-card__total-label">{tPayroll('payroll.tables.total_label')}</span>
                    <span className="payroll-table-card__total-value payroll-table-card__total-value--deductions">
                      -{fmt(stmt.deductions.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* 6개월 실수령 추이 */}
              <div className="payroll-trend" aria-labelledby="trend-h">
                <h3 id="trend-h" className="payroll-trend__title">{tPayroll('payroll.trend.title')}</h3>
                <div className="payroll-trend__bars">
                  {trend.map((t) => {
                    const heightPct = ((t.netPay - minNet) / range) * 60 + 30;
                    const isSelected = t.id === selectedId;
                    const amount10k = (t.netPay / 10000).toFixed(0);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className="payroll-trend__bar-col"
                        onClick={() => setSelectedId(t.id)}
                        aria-current={isSelected ? 'true' : undefined}
                        aria-label={tPayroll('payroll.trend.bar_aria', { label: t.label, amount: amount10k })}
                      >
                        <div className="payroll-trend__bar-value">{amount10k}{tPayroll('payroll.trend.bar_value_suffix')}</div>
                        <div className="payroll-trend__bar-track">
                          <div className="payroll-trend__bar" style={{ height: `${heightPct}%` }} />
                        </div>
                        {/* 라벨 정확 포맷 — t.month 직접 사용 (review correctness HIGH-3). */}
                        <div className="payroll-trend__bar-month">{String(t.month).padStart(2, '0')}{tPayroll('payroll.trend.bar_month_suffix')}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function PayRow({ label, amount, negate }: { label: string; amount: number; negate?: boolean }) {
  return (
    <div className="payroll-row">
      <span className="payroll-row__label">{label}</span>
      <span className={`payroll-row__value${negate ? ' payroll-row__value--deduction' : ''}`}>
        {negate ? '-' : ''}{fmt(amount)}
      </span>
    </div>
  );
}
